import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import StepNavigation from "../../components/essentials/step-navigation/StepNavigation";
import Header from "../../components/header/Header";
import "./CompleteProfile.scss";

const CompleteProfile: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const location = useLocation();

	// Define the complete profile steps
	const stepRoutes = useMemo(
		() => [
			{
				path: "/type",
				title: t("signUp.steps.accountType", "Account Type"),
			},
			{
				path: "/info",
				title: t("signUp.steps.profileInfo", "Profile Info"),
			},
			{
				path: "/sports",
				title: t("signUp.steps.sportsInfo", "Sports Info"),
			},
		],
		[t]
	);

	// Determine current step based on location
	const getCurrentStep = () => {
		const currentPath = location.pathname;
		const stepIndex = stepRoutes.findIndex((route) =>
			currentPath.includes(route.path)
		);
		return stepIndex !== -1 ? stepIndex + 1 : 1;
	};

	const currentStep = getCurrentStep();

	// Handle step navigation - allow navigation to previous steps
	const handleStepClick = (step: number) => {
		if (step <= currentStep) {
			navigate(stepRoutes[step - 1].path);
		}
	};

	// Handle next button
	const handleNext = () => {
		// For step 1 (account type), trigger form submission
		if (currentStep === 1) {
			const form = document.getElementById(
				"choose-account-type-form"
			) as HTMLFormElement;
			if (form) {
				form.requestSubmit();
			}
			return;
		}

		// For step 2 (profile data), trigger form submission
		if (currentStep === 2) {
			const form = document.getElementById(
				"complete-profile-data-form"
			) as HTMLFormElement;
			if (form) {
				form.requestSubmit();
			}
			return;
		}

		// For other steps, just navigate
		if (currentStep < stepRoutes.length) {
			navigate(stepRoutes[currentStep].path);
		}
	};

	// Handle previous button - allow going back
	const handlePrev = () => {
		if (currentStep > 1) {
			navigate(stepRoutes[currentStep - 2].path);
		}
	};

	// Handle submit button (on last step)
	const handleSubmit = () => {
		// Trigger form submission for sports info
		const form = document.getElementById(
			"complete-sports-info-form"
		) as HTMLFormElement;
		if (form) {
			form.requestSubmit();
		}
	};

	// Get visited steps (all steps up to current step)
	const visitedSteps = useMemo(() => {
		const visited = new Set<number>();
		for (let i = 1; i <= currentStep; i++) {
			visited.add(i);
		}
		return visited;
	}, [currentStep]);

	// Simple completion status - completed if we've passed the step
	const getStepCompletionStatus = (
		step: number
	): "complete" | "partial" | "incomplete" => {
		if (step < currentStep) return "complete";
		if (step === currentStep) return "incomplete";
		return "incomplete";
	};

	const hasStepData = (step: number): boolean => {
		return step < currentStep;
	};

	// Redirect to first step if on base route
	useEffect(() => {
		if (location.pathname === "/" || location.pathname === "/type") {
			navigate("/type");
		}
	}, [location.pathname, navigate]);

	return (
		<>
			<div className="container-fluid h-100 p-0 complete-profile">
				<div className="row h-100">
					<div className="col-lg-6">
						<Header
							className="complete-profile__header"
							showEmptyHeader={true}
						>
							{t("profile.headerTitle", "Complete Your Profile")}
						</Header>
						<div className="complete-profile-wrapper">
							<StepNavigation
								currentStep={currentStep}
								totalSteps={stepRoutes.length}
								stepTitles={stepRoutes.map((route) => route.title)}
								onStepClick={handleStepClick}
								visitedSteps={visitedSteps}
								hasStepData={hasStepData}
								getStepCompletionStatus={getStepCompletionStatus}
								showNavigationButtons={true}
								onNext={handleNext}
								onPrev={handlePrev}
								onSubmit={handleSubmit}
								nextLabel={t("common.buttons.next", "Next")}
								prevLabel={t("common.buttons.previous", "Previous")}
								submitLabel={t("common.buttons.submit", "Submit")}
							>
								<Outlet />
							</StepNavigation>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default CompleteProfile;
